import dayjs from 'dayjs';
import CalendarConfig from '../../../consts/CalendarConfig';
import { MODULE_KEYS, parseComplaintsList } from './appointmentModules';
import { filterEvolutionPhotos, resolveComparison, sortByDateAsc } from './evolutionPhotos';
import { variantFromGender } from '../../../components/bodySilhouette/bodySilhouetteFigures';
import { findOwnRecord as findOwnBodyMapRecord, sortByDateDesc as sortBodyMapRecords } from '../components/bodyMap/bodyMapMarkers';
import { findOwnRecord as findOwnApplicationRecord, sortByDateDesc as sortApplicationRecords } from '../components/applicationMap/applicationMarks';
import { loadPhotoFigures } from '../components/applicationMap/loadPhotoFigures';
import { REFERENCE_PREVIOUS, findReference, sortByDateDesc as sortMeasurements } from '../components/bodyMeasurements/bodyMeasurementsMath';
import { getCurrentPlan, normalizeMeals } from '../components/mealPlan/mealPlanModel';
import { buildCranialPhotoReport, listCranialReportPhotoRequests } from '../components/cranialAsymmetry/cranialAsymmetryReportAdapter';
import { filterCranialPhotos } from '../components/cranialAsymmetry/photos/cranialPhotoViews';
import { anamnesisTemplateLabel, parseAnamnesisAnswers } from '../components/anamneseHistory/anamnesisAnswers';
import { loadReportPhotoUrls } from '../../../services/clinicalReportService';
import ComplaintsReportBody from '../../../components/documents-templates/report/bodies/ComplaintsReportBody';
import EvolutionReportBody from '../../../components/documents-templates/report/bodies/EvolutionReportBody';
import NotesReportBody from '../../../components/documents-templates/report/bodies/NotesReportBody';
import AnamnesisReportBody from '../../../components/documents-templates/report/bodies/AnamnesisReportBody';
import CranialAsymmetryReportBody from '../../../components/documents-templates/report/bodies/CranialAsymmetryReportBody';
import { EvolutionPhotosReportBody, evolutionPhotosReportMeta } from '../../../components/documents-templates/EvolutionPhotosReportTemplate';
import { BodyMapReportBody, bodyMapReportMeta } from '../../../components/documents-templates/BodyMapReportTemplate';
import { BodyMeasurementsReportBody, bodyMeasurementsReportMeta } from '../../../components/documents-templates/BodyMeasurementsReportTemplate';
import { ApplicationMapReportBody, applicationMapReportMeta } from '../../../components/documents-templates/ApplicationMapReportTemplate';
import { MealPlanReportBody, mealPlanReportMeta } from '../../../components/documents-templates/MealPlanReportTemplate';
import ScaleReportBody from '../../../components/documents-templates/report/bodies/ScaleReportBody';
import GrowthCurveReportBody from '../../../components/documents-templates/report/bodies/GrowthCurveReportBody';
import { cranialAges, growthStatsList } from './growth/growthSeries';
import MotorMilestonesReportBody from '../../../components/documents-templates/report/bodies/MotorMilestonesReportBody';
import { ageAt, milestoneRows, prematurityDays, todayIso } from './motorMilestones/motorMilestones';
import ThermographyReportBody from '../../../components/documents-templates/report/bodies/ThermographyReportBody';
import { buildGroups, countAsymmetries, countSites, findOwnRecord as findOwnThermography, formatDate as formatThermographyDate, previousOf, sortByDateDesc as sortThermography } from './thermography/thermography';
import AimsReportBody from '../../../components/documents-templates/report/bodies/AimsReportBody';
import { buildRows as buildAimsRows, findOwnRecord as findOwnAims, formatDate as formatAimsDate, sortByDateDesc as sortAims } from './aims/aims';
import { SCALES } from './scales';
import { findOwnRecord as findOwnScaleRecord, scaleRecords } from './scales/scaleValues';
import DiagnosesReportBody from '../../../components/documents-templates/report/bodies/DiagnosesReportBody';
import { groupByAppointment } from './diagnoses/diagnoses';

/**
 * Seções do relatório em PDF, uma por módulo do atendimento. Cada seção sabe
 * escolher o registro que vai para o papel (`select`), baixar o que precisa
 * (`load`, opcional: fotos), descrever o cabeçalho (`meta`) e desenhar o
 * miolo (`Body`). Os cards usam os mesmos `build*Job` nos seus botões "PDF",
 * então o relatório completo imprime exatamente o que cada card imprimiria.
 *
 * Contexto (`ctx`) passado a `select`/`load`:
 * - `data`: resposta do atendimento (history, notes, anamneses, cranialAsymetry,
 *   bodyMeasurements, bodyMap, mealPlans, applicationMaps, scaleAssessments, growthMeasurements,
 *   motorMilestones, birthInfo, thermography, aimsAssessments, diagnoses, patientAttachments);
 * - `patient` / `appointment`: paciente e agendamento (`appointment` nulo na aba Saúde);
 * - `companyConfig`: CompanyConfig (modelos de anamnese, profissionais);
 * - `complaints`: lista de queixas em edição na tela (opcional; sem ela lê de `data`);
 * - `loaders.GetPatientAttachmentFile`: download de anexos para as fotos.
 *
 * Documentos e arquivos ficam de fora: são anexos, não registros clínicos.
 */

const today = () => dayjs().format('DD/MM/YYYY');
const count = (n, singular, plural) => `${n} ${n === 1 ? singular : plural}`;
const isAnnulled = (record) => record.isAnnulled === '1' || record.isAnnulled === 1;

// Mesma limpeza do card de queixas: tira HTML e troca o "\n" literal por quebra
const cleanAssessment = (text) => (text || '').replace(/<[^>]*>?/gm, '').replace(/\\n/g, '\n');

/** Primeira avaliação registrada (última posição do histórico, como no card). */
export function firstAssessment(history) {
  const list = Array.isArray(history) ? history : [];
  const last = list[list.length - 1];
  return last && last.assessment ? cleanAssessment(last.assessment) : '';
}

// ─── Montagem dos jobs (compartilhada com os cards) ────────────────────────

export function buildComplaintsJob(list, history) {
  const complaints = (Array.isArray(list) ? list : []).filter(Boolean);
  const assessment = firstAssessment(history);
  if (complaints.length === 0 && !assessment) return null;
  return { complaints, assessment };
}

/** `diagnoses` (opcional): diagnósticos do paciente, impressos junto da evolução da consulta. */
export function buildEvolutionJob(records, diagnoses) {
  const list = (Array.isArray(records) ? records : []).filter((record) => !isAnnulled(record));
  return list.length > 0 ? { records: list, diagnoses: Array.isArray(diagnoses) ? diagnoses : [] } : null;
}

export function buildNotesJob(notes, appointmentId) {
  const list = (Array.isArray(notes) ? notes : []).filter((note) => note && note.note);
  return list.length > 0 ? { notes: list, appointmentId: appointmentId ?? null } : null;
}

export function buildAnamnesisJob(anamnese, companyConfig) {
  if (!anamnese) return null;
  return {
    title: anamnesisTemplateLabel(companyConfig, anamnese),
    date: anamnese.createdAt ? CalendarConfig.GetDDMMAAAA(anamnese.createdAt) : '',
    items: parseAnamnesisAnswers(anamnese),
  };
}

export function buildBodyMapJob(record, variant) {
  const markers = record && Array.isArray(record.markers) ? record.markers : [];
  if (markers.length === 0) return null;
  return { record: { markedAt: record.markedAt || '', markers }, variant };
}

export async function buildApplicationMapJob(loader, record, variant) {
  const marks = record && Array.isArray(record.marks) ? record.marks : [];
  if (marks.length === 0) return null;
  const backgrounds = record.backgrounds && typeof record.backgrounds === 'object' && !Array.isArray(record.backgrounds)
    ? record.backgrounds
    : {};
  return {
    record: { markedAt: record.markedAt || '', marks, batch: record.batch || '', notes: record.notes || '' },
    variant,
    photoFigures: await loadPhotoFigures(loader, backgrounds),
  };
}

export function buildBodyMeasurementsJob(history, current, reference, variant, birthday) {
  if (!current) return null;
  return { history, current, reference, variant, birthday: birthday || null };
}

export async function buildEvolutionPhotosJob(loader, pair) {
  if (!pair || !pair.before) return null;
  const selected = [pair.before, pair.after].filter(Boolean);
  const urls = await loadReportPhotoUrls(loader, selected.map((photo) => ({ id: photo.id, size: 'full' })));
  return { before: pair.before, after: pair.after, urls };
}

export async function buildCranialJob(loader, history, attachments) {
  const list = Array.isArray(history) ? history : [];
  if (list.length === 0) return null;
  const photos = filterCranialPhotos(attachments || []);
  const base = buildCranialPhotoReport(list, photos);
  const urls = base ? await loadReportPhotoUrls(loader, listCranialReportPhotoRequests(base)) : null;
  return { history: list, photoReport: base ? buildCranialPhotoReport(list, photos, urls) : null };
}

/**
 * Curva de crescimento: estatística de todas as medições do paciente e as
 * idades das medições cranianas (marcadores no gráfico do PC).
 * `patient`: precisa de `birthday` e `gender`.
 */
export function buildGrowthCurveJob(measurements, patient, cranial) {
  const list = Array.isArray(measurements) ? measurements : [];
  if (list.length === 0) return null;
  const birthday = patient && patient.birthday;
  const gender = patient && patient.gender;
  return {
    statsList: growthStatsList(list, birthday, gender),
    gender,
    markers: cranialAges(cranial, birthday),
  };
}

/**
 * Marcos motores: uma linha por marco do catálogo, com a situação frente às
 * janelas da OMS na data de hoje, e a idade atual (corrigida se prematuro).
 * Sem marco registrado devolve null, a não ser com `allowEmpty` (o card mostra
 * o que esperar para a idade mesmo sem registro).
 * `patient`: precisa de `birthday`; `birthInfo`: idade gestacional ou null.
 */
export function buildMotorMilestonesJob(records, patient, birthInfo, { allowEmpty = false } = {}) {
  const list = Array.isArray(records) ? records : [];
  if (list.length === 0 && !allowEmpty) return null;
  const birthday = patient && patient.birthday;
  const today = todayIso();
  return {
    rows: milestoneRows(list, birthday, birthInfo, today),
    current: ageAt(birthday, today, birthInfo),
    corrected: prematurityDays(birthInfo) > 0,
    birthInfo: birthInfo || null,
    total: list.length,
  };
}

/**
 * Termografia: a avaliação em destaque, comparada com a imediatamente
 * anterior do paciente. `history`: todas as avaliações do paciente.
 */
export function buildThermographyJob(history, record) {
  if (!record) return null;
  const previous = previousOf(sortThermography(history), record);
  const groups = buildGroups(record.readings, previous && previous.readings);
  return {
    record,
    previous,
    groups,
    sites: countSites(groups),
    asymmetries: countAsymmetries(groups),
  };
}

/**
 * Escala de Alberta: o histórico do paciente (mais recente primeiro) e a
 * avaliação em destaque (`record`, senão a mais recente). `patient` precisa de
 * `birthday`; `birthInfo`: idade gestacional ou null.
 */
export function buildAimsJob(history, patient, birthInfo, record) {
  const rows = buildAimsRows(history, patient && patient.birthday, birthInfo);
  if (rows.length === 0) return null;
  const featured = (record && rows.find((row) => String(row.record.id) === String(record.id))) || rows[0];
  return { rows, featured, birthInfo: birthInfo || null };
}

/** Diagnósticos do paciente (todos os atendimentos, o atual marcado). */
export function buildDiagnosesJob(diagnoses, appointmentId) {
  const list = Array.isArray(diagnoses) ? diagnoses : [];
  return list.length > 0 ? { diagnoses: list, appointmentId: appointmentId ?? null } : null;
}

// ─── Escolha do registro por módulo ─────────────────────────────────────────

const latestCranialDate = (history) => {
  const sorted = [...history].sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf());
  return sorted[0] ? dayjs(sorted[0].createdAt).format('DD/MM/YYYY') : '';
};

/** Registro deste atendimento ou, sem ele (aba Saúde, atendimento sem mapa), o mais recente. */
const ownOrLatest = (records, appointment, findOwn) => {
  if (records.length === 0) return null;
  const own = appointment && appointment.id ? findOwn(records, appointment.id) : null;
  return own || records[0];
};

const loader = (ctx) => ctx.loaders && ctx.loaders.GetPatientAttachmentFile;

/** Escala clínica: o registro em destaque e a evolução da escala. */
export function buildScaleJob(scaleKey, records, current) {
  return current ? { scaleKey, current, records } : null;
}

/** Uma seção por escala do catálogo, todas com o mesmo miolo. */
const scaleSection = (scale) => ({
  key: scale.key,
  title: scale.name,
  reportType: `escala-${scale.key.replace(/_/g, '-')}`,
  select: (ctx) => {
    const records = scaleRecords(ctx.data && ctx.data.scaleAssessments, scale.key);
    return buildScaleJob(scale.key, records, ownOrLatest(records, ctx.appointment, findOwnScaleRecord));
  },
  meta: (job) => {
    const date = CalendarConfig.GetDDMMAAAA(job.current.assessedAt);
    return { subtitle: `Avaliação de ${date} · ${count(job.records.length, 'avaliação', 'avaliações')}`, date };
  },
  Body: ScaleReportBody,
});
const variantOf = (ctx) => variantFromGender(ctx.patient && ctx.patient.gender);

export const REPORT_SECTIONS = Object.freeze({
  [MODULE_KEYS.COMPLAINTS]: {
    key: MODULE_KEYS.COMPLAINTS,
    title: 'Queixas principais',
    reportType: 'queixas',
    select: (ctx) => buildComplaintsJob(ctx.complaints ?? parseComplaintsList(ctx.data), ctx.data && ctx.data.history),
    meta: (job) => ({ subtitle: count(job.complaints.length, 'queixa registrada', 'queixas registradas'), date: today() }),
    Body: ComplaintsReportBody,
  },
  [MODULE_KEYS.EVOLUTION]: {
    key: MODULE_KEYS.EVOLUTION,
    title: 'Evolução do paciente',
    reportType: 'evolucao',
    select: (ctx) => buildEvolutionJob(ctx.data && ctx.data.history, ctx.data && ctx.data.diagnoses),
    meta: (job) => ({ subtitle: count(job.records.length, 'registro', 'registros'), date: today() }),
    Body: EvolutionReportBody,
  },
  [MODULE_KEYS.NOTES]: {
    key: MODULE_KEYS.NOTES,
    title: 'Anotações',
    reportType: 'anotacoes',
    select: (ctx) => buildNotesJob(ctx.data && ctx.data.notes, ctx.appointment && ctx.appointment.id),
    meta: (job) => ({ subtitle: count(job.notes.length, 'anotação', 'anotações'), date: today() }),
    Body: NotesReportBody,
  },
  [MODULE_KEYS.ANAMNESIS]: {
    key: MODULE_KEYS.ANAMNESIS,
    title: 'Anamnese',
    reportType: 'anamnese',
    select: (ctx) => {
      const list = (ctx.data && ctx.data.anamneses) || [];
      const appointmentId = ctx.appointment && ctx.appointment.id;
      const own = appointmentId != null
        ? list.find((item) => item.appointmentId != null && String(item.appointmentId) === String(appointmentId))
        : null;
      return buildAnamnesisJob(own || list[0], ctx.companyConfig);
    },
    meta: (job) => ({ subtitle: [job.title, job.date].filter(Boolean).join(' · '), date: job.date }),
    Body: AnamnesisReportBody,
  },
  [MODULE_KEYS.CRANIAL_ASYMMETRY]: {
    key: MODULE_KEYS.CRANIAL_ASYMMETRY,
    title: 'Assimetria craniana',
    reportType: 'assimetria-craniana',
    select: (ctx) => {
      const history = ctx.data && ctx.data.cranialAsymetry;
      return Array.isArray(history) && history.length > 0 ? history : null;
    },
    load: (history, ctx) => buildCranialJob(loader(ctx), history, ctx.data && ctx.data.patientAttachments),
    meta: (job) => ({
      subtitle: `Avaliação de ${latestCranialDate(job.history)} · ${count(job.history.length, 'avaliação', 'avaliações')}`,
      date: latestCranialDate(job.history),
    }),
    Body: CranialAsymmetryReportBody,
  },
  [MODULE_KEYS.GROWTH_CURVE]: {
    key: MODULE_KEYS.GROWTH_CURVE,
    title: 'Curva de crescimento',
    reportType: 'curva-de-crescimento',
    select: (ctx) => buildGrowthCurveJob(ctx.data && ctx.data.growthMeasurements, ctx.patient, ctx.data && ctx.data.cranialAsymetry),
    meta: (job) => {
      const last = job.statsList[job.statsList.length - 1];
      const date = CalendarConfig.GetDDMMAAAA(last.measurement.measuredAt);
      return { subtitle: `Medição de ${date} · ${count(job.statsList.length, 'medição', 'medições')}`, date };
    },
    Body: GrowthCurveReportBody,
  },
  [MODULE_KEYS.MOTOR_MILESTONES]: {
    key: MODULE_KEYS.MOTOR_MILESTONES,
    title: 'Marcos motores',
    reportType: 'marcos-motores',
    select: (ctx) => buildMotorMilestonesJob(ctx.data && ctx.data.motorMilestones, ctx.patient, ctx.data && ctx.data.birthInfo),
    meta: (job) => ({ subtitle: `Situação em ${today()} · ${count(job.total, 'marco registrado', 'marcos registrados')}`, date: today() }),
    Body: MotorMilestonesReportBody,
  },
  [MODULE_KEYS.THERMOGRAPHY]: {
    key: MODULE_KEYS.THERMOGRAPHY,
    title: 'Termografia',
    reportType: 'termografia',
    select: (ctx) => {
      const history = (ctx.data && ctx.data.thermography) || [];
      return buildThermographyJob(history, ownOrLatest(sortThermography(history), ctx.appointment, findOwnThermography));
    },
    meta: (job) => {
      const date = formatThermographyDate(job.record.measuredAt);
      return { subtitle: `Avaliação de ${date} · ${count(job.sites, 'local', 'locais')}`, date };
    },
    Body: ThermographyReportBody,
  },
  [MODULE_KEYS.AIMS]: {
    key: MODULE_KEYS.AIMS,
    title: 'Escala motora de Alberta (AIMS)',
    reportType: 'escala-de-alberta',
    select: (ctx) => {
      const history = (ctx.data && ctx.data.aimsAssessments) || [];
      return buildAimsJob(history, ctx.patient, ctx.data && ctx.data.birthInfo, ownOrLatest(sortAims(history), ctx.appointment, findOwnAims));
    },
    meta: (job) => {
      const date = formatAimsDate(job.featured.record.assessedAt);
      return { subtitle: `Avaliação de ${date} · ${count(job.rows.length, 'avaliação', 'avaliações')}`, date };
    },
    Body: AimsReportBody,
  },
  [MODULE_KEYS.DIAGNOSES]: {
    key: MODULE_KEYS.DIAGNOSES,
    title: 'Diagnósticos (CID)',
    reportType: 'diagnosticos-cid',
    select: (ctx) => buildDiagnosesJob(ctx.data && ctx.data.diagnoses, ctx.appointment && ctx.appointment.id),
    meta: (job) => {
      const groups = groupByAppointment(job.diagnoses);
      return {
        subtitle: `${count(job.diagnoses.length, 'diagnóstico', 'diagnósticos')} em ${count(groups.length, 'atendimento', 'atendimentos')}`,
        date: today(),
      };
    },
    Body: DiagnosesReportBody,
  },
  [MODULE_KEYS.EVOLUTION_PHOTOS]: {
    key: MODULE_KEYS.EVOLUTION_PHOTOS,
    title: 'Fotos de evolução',
    reportType: 'fotos-de-evolucao',
    select: (ctx) => {
      const pair = resolveComparison(null, sortByDateAsc(filterEvolutionPhotos((ctx.data && ctx.data.patientAttachments) || [])));
      return pair.before ? pair : null;
    },
    load: (pair, ctx) => buildEvolutionPhotosJob(loader(ctx), pair),
    meta: evolutionPhotosReportMeta,
    Body: EvolutionPhotosReportBody,
  },
  [MODULE_KEYS.BODY_MAP]: {
    key: MODULE_KEYS.BODY_MAP,
    title: 'Mapa corporal',
    reportType: 'mapa-corporal',
    select: (ctx) => buildBodyMapJob(
      ownOrLatest(sortBodyMapRecords((ctx.data && ctx.data.bodyMap) || []), ctx.appointment, findOwnBodyMapRecord),
      variantOf(ctx),
    ),
    meta: bodyMapReportMeta,
    Body: BodyMapReportBody,
  },
  [MODULE_KEYS.BODY_MEASUREMENTS]: {
    key: MODULE_KEYS.BODY_MEASUREMENTS,
    title: 'Medidas corporais',
    reportType: 'medidas-corporais',
    select: (ctx) => {
      const history = (ctx.data && ctx.data.bodyMeasurements) || [];
      const current = sortMeasurements(history)[0] || null;
      const reference = current ? findReference(history, current, REFERENCE_PREVIOUS) : null;
      return buildBodyMeasurementsJob(history, current, reference, variantOf(ctx), ctx.patient && ctx.patient.birthday);
    },
    meta: bodyMeasurementsReportMeta,
    Body: BodyMeasurementsReportBody,
  },
  [MODULE_KEYS.MEAL_PLAN]: {
    key: MODULE_KEYS.MEAL_PLAN,
    title: 'Plano alimentar',
    reportType: 'plano-alimentar',
    select: (ctx) => {
      const plans = ((ctx.data && ctx.data.mealPlans) || []).map((plan) => ({ ...plan, meals: normalizeMeals(plan.meals) }));
      const plan = getCurrentPlan(plans);
      return plan ? { plan } : null;
    },
    meta: mealPlanReportMeta,
    Body: MealPlanReportBody,
  },
  [MODULE_KEYS.APPLICATION_MAP]: {
    key: MODULE_KEYS.APPLICATION_MAP,
    title: 'Mapa de aplicação',
    reportType: 'mapa-de-aplicacao',
    select: (ctx) => {
      const record = ownOrLatest(sortApplicationRecords((ctx.data && ctx.data.applicationMaps) || []), ctx.appointment, findOwnApplicationRecord);
      return record && Array.isArray(record.marks) && record.marks.length > 0 ? record : null;
    },
    load: (record, ctx) => buildApplicationMapJob(loader(ctx), record, variantOf(ctx)),
    meta: applicationMapReportMeta,
    Body: ApplicationMapReportBody,
  },
  ...Object.fromEntries(SCALES.map((scale) => [scale.key, scaleSection(scale)])),
});

// ─── Relatório completo ─────────────────────────────────────────────────────

/**
 * Ordem dos cards na tela (ver AppointmentModules.js): queixas primeiro,
 * anotações logo depois quando não há módulo de evolução, e os demais na
 * ordem do tipo de atendimento.
 */
export function orderForReport(modules) {
  const list = Array.isArray(modules) ? modules : [];
  const ordered = [];
  if (list.includes(MODULE_KEYS.COMPLAINTS)) ordered.push(MODULE_KEYS.COMPLAINTS);
  if (list.includes(MODULE_KEYS.NOTES) && !list.includes(MODULE_KEYS.EVOLUTION)) ordered.push(MODULE_KEYS.NOTES);
  list.forEach((key) => {
    if (REPORT_SECTIONS[key] && !ordered.includes(key)) ordered.push(key);
  });
  return ordered;
}

/** Seções com algo a exportar, na ordem do relatório (sem baixar nada). */
export function listAvailableSections(modules, ctx) {
  return orderForReport(modules)
    .map((key) => REPORT_SECTIONS[key])
    .filter((section) => section.select(ctx) != null);
}

/** Subtítulo da seção no relatório completo (inclui o título do registro quando difere, caso do plano). */
function sectionSubtitle(section, meta) {
  const parts = [];
  if (meta.title && meta.title !== section.title) parts.push(meta.title);
  if (meta.subtitle) parts.push(meta.subtitle);
  return parts.join(' · ');
}

/**
 * Prepara todas as seções em paralelo. Devolve `[{ key, title, subtitle, Body, job }]`
 * na ordem dos cards, ou null quando nenhum módulo tem registro.
 */
export async function prepareFullReport(modules, ctx) {
  const sections = await Promise.all(orderForReport(modules).map(async (key) => {
    const section = REPORT_SECTIONS[key];
    const selected = section.select(ctx);
    if (selected == null) return null;
    const job = section.load ? await section.load(selected, ctx) : selected;
    if (!job) return null;
    const meta = section.meta ? section.meta(job) : {};
    return { key, title: section.title, subtitle: sectionSubtitle(section, meta), Body: section.Body, job };
  }));
  const ready = sections.filter(Boolean);
  return ready.length > 0 ? ready : null;
}
